import axios from 'axios';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { containerOne, title } from '../utils/style';
import { url } from '../utils/config';
import BookingForm from './BookingForm';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import appointmentDetailsAtom from '../atoms/appointmentDetailsAtom';
import fleetAppointmentDetailsAtom from '../atoms/fleetAppointmentDetailsAtom';
import globalConfigAtom from '../atoms/globalConfigAtom';
import workOrderValueAtom from '../atoms/workOrderValueAtom';
import { toast } from 'react-toastify';
import loaderStateAtom from '../atoms/loaderStateAtom';
import addIcon from '../assets/icons/add_icon.png'
import AppointmentCard from '../reusable_components/AppointmentCard';
import EquipmentCard from './EquipmentCard';
import AppointmentDetails from './AppointmentDetails';
import navHeaderAtom from '../atoms/navHeaderAtom';
import ToggleButton from "./ToggleButton";
import toggleAtom from '../atoms/toggleAtom';
import FleetAppointmentCard from './B2B/FleetAppointmentCard';
import FleetAppointmentDetails from './B2B/FleetAppointmentDetails';
import { SlCalender } from "react-icons/sl";
import { FunnelIcon } from '@heroicons/react/24/outline';

function Appointments() {
    const navigate = useNavigate();
    const [appointments, setAppointments] = useState([]);
    const [fleetAppointments, setFleetAppointments] = useState([]);
    const [availableEquipments, setAvailableEquipments] = useState([]);
    const [showExtendBookingForm, setShowExtendBookingForm] = useState(false);
    const [equipmentIndex, setEquipmentIndex] = useState();
    const [showAppointmentDetails, setShowAppointmentDetails] = useState(false);
    const [showFleetAppointmentDetails, setShowFleetAppointmentDetails] = useState(false);
    const [showCreateNewAppointment, setShowCreateNewAppointment] = useState(false);
    const [workOrder, setWorkOrder] = useRecoilState(workOrderValueAtom);
    const [showLoader, setShowLoader] = useRecoilState(loaderStateAtom)
    const [appointmentDetails, setAppointmentDetails] = useRecoilState(appointmentDetailsAtom)
    const [fleetAppointmentDetails, setFleetAppointmentDetails] = useRecoilState(fleetAppointmentDetailsAtom)
    const [header, setHeader] = useRecoilState(navHeaderAtom)
    const toggleValue = useRecoilValue(toggleAtom)
    const setGlobalConfig = useSetRecoilState(globalConfigAtom);
    const [isNextPage, setIsNextPage] = useState(false);
    const [totalPage, setTotalPage] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchId, setSearchId] = useState('');
    const [showDateFilter, setShowDateFilter] = useState(false);
    const [dateFilter, setDateFilter] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    
    // Fleet appointments pagination/search/filter states
    const [fleetIsNextPage, setFleetIsNextPage] = useState(false);
    const [fleetTotalPage, setFleetTotalPage] = useState(0);
    const [fleetCurrentPage, setFleetCurrentPage] = useState(1);
    const [fleetSearchId, setFleetSearchId] = useState('');
    const [fleetShowDateFilter, setFleetShowDateFilter] = useState(false);
    const [fleetDateFilter, setFleetDateFilter] = useState('');
    const [fleetSelectedDate, setFleetSelectedDate] = useState('');

    useEffect(() => {
        if (showAppointmentDetails) {
            setHeader('Manage Appointment')
        }
        else if (showCreateNewAppointment) {
            setHeader('Create Appointment')
        }
        else {
            setHeader('Roll & Charge')
        }
    }, [showAppointmentDetails, showCreateNewAppointment])


    useEffect(() => {
        getGlobalConfig()
        getAppointments();
        getFleetAppointments();
        getAvailableEquipments();
    }, [])

    useEffect(() => {
        // Auto search when searchId changes with debounce for B2C
        const delaySearch = setTimeout(() => {
            if (toggleValue === 'b2c') {
                if (searchId.trim()) {
                    getAppointments(1, searchId.trim(), dateFilter);
                } else {
                    getAppointments(1, '', dateFilter);
                }
            }
        }, 500);

        return () => clearTimeout(delaySearch);
    }, [searchId, dateFilter, toggleValue])

    useEffect(() => {
        // Auto search when fleetSearchId changes with debounce for B2B
        const delaySearch = setTimeout(() => {
            if (toggleValue === 'b2b') {
                if (fleetSearchId.trim()) {
                    getFleetAppointments(1, fleetSearchId.trim(), fleetDateFilter);
                } else {
                    getFleetAppointments(1, '', fleetDateFilter);
                }
            }
        }, 500);

        return () => clearTimeout(delaySearch);
    }, [fleetSearchId, fleetDateFilter, toggleValue])

    function formatDisplayDate(date) {
        if (!date) return 'Filter Date';
        const dateObj = new Date(date);
        return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    function handleDateFilterChange(e) {
        setSelectedDate(e.target.value);
    }

    function handleApplyDateFilter() {
        if (selectedDate) {
            setDateFilter(selectedDate);
            setShowDateFilter(false);
        }
    }

    function handleClearFilter() {
        setSelectedDate('');
        setDateFilter('');
        setShowDateFilter(false);
    }

    async function getGlobalConfig() {
        try {
            const reqUrl = url + '/global_config';
            const token = window.localStorage.getItem('token');
            const headers = { authorization: `bearer ${token}` };
            const data = await axios.get(reqUrl, { headers });
            setGlobalConfig(data.data.data)
        } catch (error) {
            toast.error(error.response.data.message, { autoClose: 3000 })
        }
    }

    async function getAppointments(page = 1, id = '', date = '') {
        try {
            setShowLoader(true)
            // Build query parameters
            let queryParam = '';
            if (id) {
                queryParam = `id=${id}`;
            } else {
                queryParam = `page=${page}`;
            }
            if (date) {
                queryParam += `${queryParam ? '&' : ''}date=${date}`;
            }
            const reqUrl = url + `/appointments?${queryParam}`;
            const token = window.localStorage.getItem('token');
            const headers = { authorization: `bearer ${token}` };
            const response = await axios.get(reqUrl, { headers });

            // Handle the new API response structure
            const responseData = response.data;
            setAppointments(responseData.data || []);
            setIsNextPage(responseData.isNextPage || false);
            setTotalPage(responseData.totalPage || 0);
            setCurrentPage(page);

            setShowLoader(false)
        } catch (error) {
            setShowLoader(false)
            console.error('Error fetching appointments:', error);
            toast.error(error.response?.data?.message || 'Error fetching appointments', { autoClose: 3000 })
        }
    }

    function handleSearch() {
        if (searchId.trim()) {
            getAppointments(1, searchId.trim());
        } else {
            getAppointments(1);
        }
    }

    function clearSearch() {
        setSearchId('');
        getAppointments(1);
    }

    function loadNextPage() {
        if (isNextPage && !searchId) {
            getAppointments(currentPage + 1, '', dateFilter);
        }
    }

    function loadPreviousPage() {
        if (currentPage > 1 && !searchId) {
            getAppointments(currentPage - 1, '', dateFilter);
        }
    }

    async function getFleetAppointments(page = 1, id = '', date = '') {
        try {
            setShowLoader(true)
            // Build query parameters
            let queryParam = '';
            if (id) {
                queryParam = `id=${id}`;
            } else {
                queryParam = `page=${page}`;
            }
            if (date) {
                queryParam += `${queryParam ? '&' : ''}date=${date}`;
            }
            const reqUrl = url + `/fleet_appointments?${queryParam}`;
            const token = window.localStorage.getItem('token');
            const headers = { authorization: `bearer ${token}` };
            const response = await axios.get(reqUrl, { headers });

            // Handle the new API response structure
            const responseData = response.data;
            setFleetAppointments(responseData.data || []);
            setFleetIsNextPage(responseData.isNextPage || false);
            setFleetTotalPage(responseData.totalPage || 0);
            setFleetCurrentPage(page);

            setShowLoader(false)
        } catch (error) {
            setShowLoader(false)
            console.error('Error fetching fleet appointments:', error);
            toast.error(error.response?.data?.message || 'Error fetching fleet appointments', { autoClose: 3000 })
        }
    }

    async function getAvailableEquipments() {
        try {
            const reqUrl = url + '/available_equipments';
            const token = window.localStorage.getItem('token');
            const headers = { authorization: `bearer ${token}` };
            const data = await axios.get(reqUrl, { headers });
            setAvailableEquipments(data.data.data)
        } catch (error) {
            toast.error(error.response.data.message, { autoClose: 3000 })
        }
    }


    function fetchAppointmentDetails(id) {
        if (!id) {
            return
        }
        setShowLoader(true)
        const reqUrl = url + '/appointment_details/' + id;
        const token = window.localStorage.getItem('token');


        const headers = { authorization: `bearer ${token}` };
        axios.get(reqUrl, { headers })
            .then(res => {
                setAppointmentDetails(res.data.data);
                let work_estimation = res.data.data.pilot_estimate;
                if (work_estimation) {
                    let serviceBundle = JSON.parse(work_estimation.bundleList);
                    let serviceList = JSON.parse(work_estimation.serviceList)
                    let materialList = JSON.parse(work_estimation.materialList)
                    let otherList = JSON.parse(work_estimation.otherList)
                    let id = work_estimation.id
                    setWorkOrder({
                        id,
                        serviceBundle,
                        serviceList,
                        materialList,
                        otherList,
                        technicianCost: appointmentDetails.technicianHourlyCost,
                        estimatedExpirationDate: work_estimation.estimatedExpirationDate,
                        estimatedCompletionDate: work_estimation.estimatedDate,
                        estimatedCompletionTime: work_estimation.estimatedTime,
                        uploadedImages: work_estimation.images || []
                    })
                }
                setShowLoader(false)
                setShowAppointmentDetails(true)
            })
            .catch(error => {
                setShowLoader(true)
                toast.error(error?.response?.data?.message || '', { autoClose: 3000 })
            })
    }

    function handleBackFromAppointmentDetails() {
        setShowAppointmentDetails(false);
        setSearchId('');
        setDateFilter('');
        setSelectedDate('');
        setShowDateFilter(false);
        getAppointments(1);
    }

    function handleBackFromFleetAppointmentDetails() {
        setShowFleetAppointmentDetails(false);
        setFleetSearchId('');
        setFleetDateFilter('');
        setFleetSelectedDate('');
        setFleetShowDateFilter(false);
        getFleetAppointments(1);
    }

    function fetchFleetAppointmentDetails(id) {
        if (!id) {
            return
        }
        setShowLoader(true)
        const reqUrl = url + '/fleet_appointment_details/' + id;
        const token = window.localStorage.getItem('token');

        const headers = { authorization: `bearer ${token}` };
        axios.get(reqUrl, { headers })
            .then(res => {
                setFleetAppointmentDetails(res.data.data);
                setShowLoader(false)
                setShowFleetAppointmentDetails(true)
            })
            .catch(error => {
                setShowLoader(true)
                toast.error(error?.response?.data?.message || '', { autoClose: 3000 })
            })
    }


    return (
        <div className={containerOne}>
            {showExtendBookingForm && (
                <BookingForm
                    extension
                    setShowModal={setShowExtendBookingForm}
                    equipmentData={availableEquipments[equipmentIndex]}
                />
            )}

            {toggleValue === "b2c" && showAppointmentDetails && (
                <AppointmentDetails
                    fetchAppointmentDetails={fetchAppointmentDetails}
                    setShowAppointmentDetails={handleBackFromAppointmentDetails}
                    getAppointments={getAppointments}
                />
            )}

            {toggleValue === "b2b" && showFleetAppointmentDetails && (
                <FleetAppointmentDetails
                    fetchFleetAppointmentDetails={fetchFleetAppointmentDetails}
                    setShowFleetAppointmentDetails={handleBackFromFleetAppointmentDetails}
                    getFleetAppointments={getFleetAppointments}
                />
            )}

            {/* Appointment Listing and Equipment Section */}
            {(toggleValue === "b2c" || toggleValue === "b2b") &&
                !showAppointmentDetails &&
                !showFleetAppointmentDetails && (
                    <>
                        <div className="flex justify-between items-center p-2">
                            <span className={title}>All Appointments</span>
                            <ToggleButton />
                        </div>

                        {/* Search by ID for B2C */}
                        {toggleValue === "b2c" && (
                            <div className="sticky top-0 bg-white border-b p-4 z-10">
                                <div className="flex gap-2 items-center">
                                    <div className="relative flex-1">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                            />
                                        </svg>
                                        <input
                                            type="text"
                                            placeholder="ID"
                                            value={searchId}
                                            onChange={(e) => setSearchId(e.target.value)}
                                            className="w-full pl-10 pr-10 py-2 text-sm bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-customOne"
                                        />
                                        {searchId && (
                                            <button
                                                onClick={clearSearch}
                                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                                aria-label="Clear search"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="h-5 w-5"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            </button>
                                        )}
                                    </div>

                                    {/* Date Filter */}
                                    <div className="relative date-filter-container">
                                        <button
                                            onClick={() => setShowDateFilter(!showDateFilter)}
                                            className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg text-sm hover:bg-gray-100 transition-colors whitespace-nowrap"
                                        >
                                            <FunnelIcon className="h-4 w-4 text-gray-500" />
                                            <span className="text-gray-700">{formatDisplayDate(dateFilter)}</span>
                                        </button>

                                        {showDateFilter && (
                                            <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-lg border p-3 z-20 min-w-[240px]">
                                                <div className="flex flex-col space-y-2">
                                                    <input
                                                        type="date"
                                                        value={selectedDate || dateFilter}
                                                        onChange={handleDateFilterChange}
                                                        placeholder="MM-DD-YYYY"
                                                        className="p-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-customOne"
                                                        min="2000-01-01"
                                                        max="2100-12-31"
                                                    />
                                                    {(selectedDate || dateFilter) && (
                                                        <button
                                                            onClick={handleClearFilter}
                                                            className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1 justify-center py-1"
                                                        >
                                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                            Clear Filter
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={handleApplyDateFilter}
                                                        className="text-sm text-customOne hover:text-customOneDark flex items-center gap-1 justify-center py-1"
                                                    >
                                                        Apply
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => navigate('/create_new_appointment')}
                                        className="text-customOne hover:text-customOneDark text-sm font-bold whitespace-nowrap"
                                    >
                                        + New
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Search by ID for B2B */}
                        {toggleValue === "b2b" && (
                            <div className="sticky top-0 bg-white border-b p-4 z-10">
                                <div className="flex gap-2 items-center">
                                    <div className="relative flex-1">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                            />
                                        </svg>
                                        <input
                                            type="text"
                                            placeholder="ID"
                                            value={fleetSearchId}
                                            onChange={(e) => setFleetSearchId(e.target.value)}
                                            className="w-full pl-10 pr-10 py-2 text-sm bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-customOne"
                                        />
                                        {fleetSearchId && (
                                            <button
                                                onClick={() => {
                                                    setFleetSearchId('');
                                                    getFleetAppointments(1);
                                                }}
                                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                                aria-label="Clear search"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="h-5 w-5"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            </button>
                                        )}
                                    </div>

                                    {/* Date Filter for B2B */}
                                    <div className="relative date-filter-container">
                                        <button
                                            onClick={() => setFleetShowDateFilter(!fleetShowDateFilter)}
                                            className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg text-sm hover:bg-gray-100 transition-colors whitespace-nowrap"
                                        >
                                            <FunnelIcon className="h-4 w-4 text-gray-500" />
                                            <span className="text-gray-700">{formatDisplayDate(fleetDateFilter)}</span>
                                        </button>

                                        {fleetShowDateFilter && (
                                            <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-lg border p-3 z-20 min-w-[240px]">
                                                <div className="flex flex-col space-y-2">
                                                    <input
                                                        type="date"
                                                        value={fleetSelectedDate || fleetDateFilter}
                                                        onChange={(e) => setFleetSelectedDate(e.target.value)}
                                                        placeholder="MM-DD-YYYY"
                                                        className="p-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-customOne"
                                                        min="2000-01-01"
                                                        max="2100-12-31"
                                                    />
                                                    {(fleetSelectedDate || fleetDateFilter) && (
                                                        <button
                                                            onClick={() => {
                                                                setFleetSelectedDate('');
                                                                setFleetDateFilter('');
                                                                setFleetShowDateFilter(false);
                                                            }}
                                                            className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1 justify-center py-1"
                                                        >
                                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                            Clear Filter
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => {
                                                            if (fleetSelectedDate) {
                                                                setFleetDateFilter(fleetSelectedDate);
                                                                setFleetShowDateFilter(false);
                                                            }
                                                        }}
                                                        className="text-sm text-customOne hover:text-customOneDark flex items-center gap-1 justify-center py-1"
                                                    >
                                                        Apply
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => navigate('/create_new_appointment')}
                                        className="text-customOne hover:text-customOneDark text-sm font-bold whitespace-nowrap"
                                    >
                                        + New
                                    </button>
                                </div>
                            </div>
                        )}

                        {
                            toggleValue === "b2c" && appointments.length > 0 ?
                                (
                                    <>
                                        <AppointmentCard
                                            appointments={appointments}
                                            fetchAppointmentDetails={fetchAppointmentDetails}
                                        />

                                        {/* Pagination Controls - Hide when searching */}
                                        {!searchId && (
                                            <div className="sticky top-0 bg-white border-b p-4 z-10">
                                                <div className="flex justify-between items-center">
                                                    <button
                                                        onClick={loadPreviousPage}
                                                        disabled={currentPage === 1}
                                                        className={`px-4 py-2 text-sm rounded-lg font-medium transition-all ${currentPage === 1
                                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                            : 'bg-customOne text-white hover:bg-opacity-90'
                                                            }`}
                                                    >
                                                        Previous
                                                    </button>

                                                    <span className="text-sm text-gray-600">
                                                        Page {currentPage} of {totalPage}
                                                    </span>

                                                    <button
                                                        onClick={loadNextPage}
                                                        disabled={!isNextPage}
                                                        className={`px-4 py-2 text-sm rounded-lg font-medium transition-all ${!isNextPage
                                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                            : 'bg-customOne text-white hover:bg-opacity-90'
                                                            }`}
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) :
                                (
                                    toggleValue === "b2b" && fleetAppointments.length > 0
                                        ?
                                        (
                                            <FleetAppointmentCard
                                                appointments={fleetAppointments}
                                                fetchFleetAppointmentDetails={fetchFleetAppointmentDetails}
                                            />
                                        ) : (
                                            toggleValue === "b2c" ? (
                                                <div className="flex flex-col items-center justify-center py-10 bg-gray-50 rounded-2xl shadow-sm border border-gray-200">
                                                <SlCalender className="w-12 h-12 text-customOne opacity-80 mb-4" />
                                                <h2 className="text-lg font-semibold text-gray-800 mb-2">
                                                    No Appointments Found
                                                </h2>
                                                <p className="text-gray-700 text-sm mb-6 text-center max-w-sm">
                                                    It looks like you don’t have any scheduled appointments yet.
                                                    Create one now to get started.
                                                </p>
                                                <Link
                                                    to="/create_new_appointment"
                                                    onClick={() => {
                                                        setShowCreateNewAppointment(true);
                                                        setHeader('Create Appointment');
                                                    }}
                                                    className="inline-flex items-center gap-2 bg-customOne text-white text-sm font-medium px-5 py-2.5 rounded-lg shadow-sm transition-all duration-200"
                                                >
                                                    Create Appointment
                                                </Link>
                                            </div>
                                            ) : null
                                        )
                                )}

                        {/* Pagination Controls for B2B - Hide when searching */}
                        {toggleValue === "b2b" && fleetAppointments.length > 0 && !fleetSearchId && (
                            <div className="sticky top-0 bg-white border-b p-4 z-10">
                                <div className="flex justify-between items-center">
                                    <button
                                        onClick={() => {
                                            if (fleetCurrentPage > 1) {
                                                getFleetAppointments(fleetCurrentPage - 1, '', fleetDateFilter);
                                            }
                                        }}
                                        disabled={fleetCurrentPage === 1}
                                        className={`px-4 py-2 text-sm rounded-lg font-medium transition-all ${
                                            fleetCurrentPage === 1
                                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                : 'bg-customOne text-white hover:bg-opacity-90'
                                        }`}
                                    >
                                        Previous
                                    </button>

                                    <span className="text-sm text-gray-600">
                                        Page {fleetCurrentPage} of {fleetTotalPage}
                                    </span>

                                    <button
                                        onClick={() => {
                                            if (fleetIsNextPage) {
                                                getFleetAppointments(fleetCurrentPage + 1, '', fleetDateFilter);
                                            }
                                        }}
                                        disabled={!fleetIsNextPage}
                                        className={`px-4 py-2 text-sm rounded-lg font-medium transition-all ${
                                            !fleetIsNextPage
                                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                : 'bg-customOne text-white hover:bg-opacity-90'
                                        }`}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className='py-4'>
                            <span className={title}>Equipment Available</span>
                        </div>

                        <EquipmentCard equipments={availableEquipments} />

                        <div className='flex justify-center items-center p-4 rounded-lg border-2 border-dashed border-customOne mt-4'>
                            <Link to='/special_equipments'>
                                <div className='flex gap-2 justify-center items-center'>
                                    <img src={addIcon} />
                                    <span>Request Equipment</span>
                                </div>
                            </Link>
                        </div>
                    </>
                )
            }

        </div>
    );

}

export default Appointments;